//ts-nocheck
import { get } from 'lodash';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import CustomBtnSelect from '../../components/Formik/CustomBtnSelect';
import { SERVER_URL } from '../../config';
import AuthService from '../../services/authService';
import UserService from '../../services/userService';
import { updateAuthUser } from '../../store/actions/auth';
import CardList from './CardList';
import ConfirmDetails from './ConfirmDetails';
import DeleteModal from './DeleteModal';
import './insurance.scss';
import SMS from './SMS';
import { Box, Button, Typography } from '@mui/material';
import { 
  uploadImageToOgnomyServer,
  updateInsurancePhoto,
  createOrbitzPresigns 
} from 'src/services/orbitzService';
import SMSModal from './SMSModal';

const defaultDetails = {
  insuranceCarrier: '',
  insuredID: '',
  planNumber: '',
  relationship: '',
  subscriberName: '',
  effectiveDate: '',
};

export const useObjectState = (initialState: any) => {
  const [state, setState] = useState(initialState);

  const updateProperties = (update: any) => {
    if (typeof update === 'function') {
      setState((prevState: any) => ({
        ...prevState,
        ...update(prevState),
      }));
    } else {
      setState((prevState: any) => ({
        ...prevState,
        ...update,
      }));
    }
  };

  return [state, updateProperties];
};

enum Screens {
  INIT,
  DEFAULT,
  EDIT,
  MANUALADD,
  MOBILEUPLOAD,
  SELFPAY,
}

const Insurance = ({
  onContinue,
  patientInLimitedState,
  defaultCards = [],
}: any) => {
  const auth = AuthService.getAuth();
  const { user } = auth;

  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { pathname } = location;

  const insuranceCard = get(user, "insuranceCards", []);
  if (insuranceCard.length && pathname === "registration") {
    onContinue(user);
  }

  const initialScreen =
    insuranceCard.length || defaultCards.length
      ? Screens.DEFAULT
      : Screens.INIT;
  
  const [state, setState] = useObjectState({
    limitedCoverageStates: [],
    screen: initialScreen,
    medicarePartB: !!user?.medicarePartB,
    medicaid: !!user?.medicaid,
    hasInsurance: !!user?.insuranceCards.length,
    selfPay: !!user?.selfPay,
    insuranceCards: [],
    mobileUploadCompleted: false,
    openSMS: false,
    verificationCode: '',
    edittedCard: null,
    openDelete: null,
  });

  const [loadingState, setLoadingState] = useObjectState({
    observing: false,
    submittingCard: false,
    updatingUser: false,
  });

  const [formDetails, setFormDetails] = useState(defaultDetails);
  const [uploadSessionStarted, setUploadSessionStarted] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await UserService.getUserPayment(user.id);
        const insuranceCards = data.insuranceCards || [];
  
        AuthService.updateUserInfo(data);
        dispatch(updateAuthUser(data));
  
        setState({
          insuranceCards,
          hasInsurance: !!insuranceCards.length,
        });
      } catch (err) {
        console.error('Failed to fetch insurance cards:', err);
      }
    })();
  }, []);

  useEffect(() => {
    let pollingInterval: any;
  
    const shouldPoll =
      loadingState.observing &&
      state.openSMS &&
      state.edittedCard?._id;
  
    if (shouldPoll) {
      pollingInterval = setInterval(async () => {
        try {
          const { data } = await UserService.getUserPayment(user.id);
          const insuranceCards = data.insuranceCards || [];
  
          const matchedCard = insuranceCards.find(
            (c) => c._id === state.edittedCard._id
          );
  
          const frontChanged =
            matchedCard?.frontPhoto &&
            matchedCard?.frontPhoto !== state.edittedCard?.frontPhoto;
  
          const backChanged =
            matchedCard?.backPhoto &&
            matchedCard?.backPhoto !== state.edittedCard?.backPhoto;
  
          const uploadCompleted = frontChanged && backChanged;
  
          if (uploadCompleted && uploadSessionStarted) {
            clearInterval(pollingInterval);
            setUploadSessionStarted(false);
  
            if (!state.openSMS) {
              setLoadingState({ observing: false });
  
              setState((prev: any) => ({
                ...prev,
                insuranceCards,
                hasInsurance: !!insuranceCards.length,
                screen: Screens.DEFAULT,
                edittedCard: null,
              }));
            } else {
              setState((prev: any) => ({
                ...prev,
                insuranceCards,
                hasInsurance: !!insuranceCards.length,
              }));
            }
          } else {
            const hasChanged =
              JSON.stringify(state.insuranceCards) !==
              JSON.stringify(insuranceCards);
  
            if (hasChanged) {
              setState((prev: any) => ({
                ...prev,
                insuranceCards,
                hasInsurance: !!insuranceCards.length,
              }));
            }
          }
        } catch (err) {
          console.error("❌ Polling error:", err);
        }
      }, 5000);
    }
  
    return () => {
      clearInterval(pollingInterval);
    };
  }, [loadingState.observing, state.edittedCard?._id, state.openSMS]);  
  
  const handleUpdateUser = async (updates: { [key: string]: any }) => {
    setLoadingState({ updatingUser: true });
    try {
      const response = await UserService.updateProfile(updates, user.id);
      const { data } = response;
      AuthService.updateUserInfo(data);
      dispatch(updateAuthUser(data));

      setState(updates);
      return response;
    } catch (err) {
      toast.error('Unable to update profile.');
    }
    setLoadingState({ updatingUser: false });
  };

  const handleInitiateNewCard = () => {
    // Need to do check for mobile
    if (
      /iPhone/i.test(navigator.userAgent) ||
      /Android/i.test(navigator.userAgent)
    ) {
      history.push(
        `/upload-insurance?v=1${
          ['/registration', '/complete-profile'].includes(pathname)
            ? '&r=1'
            : pathname === '/scheduling'
              ? '&s=1'
              : ''
        }`,
      );
    } else {
      setState({ openSMS: true });
    }
  };

  const handleEditCard = (cardId: any) => {
    const card = state.insuranceCards.filter(
      (card: any) => card._id === cardId,
    )[0];
    if (card) {
      setState({ screen: Screens.EDIT, edittedCard: card });
    }
  };

  const handleDeleteCard = async () => {
    let insuranceCards = state.insuranceCards.filter(
      (card: any, index: any) => index !== state.openDelete,
    );
    await handleUpdateUser({ insuranceCards });
    setState({ openDelete: null });
  };

  const handleConfirm = async ({
    frontFilePath,
    backFilePath,
    details,
  }: {
    frontFilePath: File | null;
    backFilePath: File | null;
    details: any;
  }): Promise<any | null> => {
    const hasFront = frontFilePath || details.frontPhoto;
    const hasBack = backFilePath || details.backPhoto;
  
    const bothMissing = !hasFront && !hasBack;
    const onlyOnePresent = (hasFront && !hasBack) || (!hasFront && hasBack);
  
    if (onlyOnePresent) {
      toast.warning('Please upload both front and back images for your insurance card.');
      return null;
    }
  
    try {
      setLoadingState({ submittingCard: true });
  
      // Track current card IDs before update
      const prevIds = state.insuranceCards.map((c: any) => c._id);
  
      let frontImagePath = details.frontPhoto || '';
      let backImagePath = details.backPhoto || '';
      let frontPresignData = {};
      let backPresignData = {};
  
      const shouldUploadFront = !!frontFilePath;
      const shouldUploadBack = !!backFilePath;
  
      if (shouldUploadFront || shouldUploadBack) {
        const presigns = await createOrbitzPresigns();
        if (!presigns) throw new Error('Missing presigned URLs');
  
        if (shouldUploadFront && presigns.front) {
          await updateInsurancePhoto(presigns.front.presign, frontFilePath!);
          const bodyFront = new FormData();
          bodyFront.append('1', frontFilePath!);
          if (user) bodyFront.append('verified', 'true');
          const resFront = await uploadImageToOgnomyServer(bodyFront);
          frontImagePath = resFront?.data?.['1'] || '';
          frontPresignData = {
            frontPresign: presigns.front.presign,
            frontFileId: presigns.front.fileId,
          };
        }
  
        if (shouldUploadBack && presigns.back) {
          await updateInsurancePhoto(presigns.back.presign, backFilePath!);
          const bodyBack = new FormData();
          bodyBack.append('2', backFilePath!);
          if (user) bodyBack.append('verified', 'true');
          const resBack = await uploadImageToOgnomyServer(bodyBack);
          backImagePath = resBack?.data?.['2'] || '';
          backPresignData = {
            backPresign: presigns.back.presign,
            backFileId: presigns.back.fileId,
          };
        }
      }
  
      const cardToUpdate = state.edittedCard || {};
      const updatedCard = {
        ...cardToUpdate,
        ...details,
        frontPhoto: frontImagePath,
        backPhoto: backImagePath,
        ...frontPresignData,
        ...backPresignData,
      };
  
      let updatedCards = state.insuranceCards.map((card: any) =>
        card._id === updatedCard._id ? updatedCard : card
      );
  
      // If new card
      if (!updatedCard._id) {
        updatedCards.push(updatedCard);
      }
  
      const response = await handleUpdateUser({ insuranceCards: updatedCards });
      const updatedInsuranceCards = get(response, 'data.insuranceCards', []);
      const finalCard = updatedInsuranceCards.find(
        (card: any) => !prevIds.includes(card._id)
      );
  
      setState({
        insuranceCards: updatedInsuranceCards,
        screen: Screens.DEFAULT,
        edittedCard: null,
      });
  
      toast[
        bothMissing ? 'warning' : 'success'
      ](
        bothMissing
          ? 'Submitting without images. Please provide images of your insurance card.'
          : 'Insurance card saved successfully.'
      );
  
      return finalCard || null;
    } catch (err) {
      console.error('Image upload error:', err);
      toast.error('Failed to save insurance card. Please try again.');
      return null;
    } finally {
      setLoadingState({ submittingCard: false });
    }
  };

  const startPollingForExistingCard = () => {
    setUploadSessionStarted(true);
    setLoadingState((prev: any) => ({
      ...prev,
      observing: true,
    }));
  };  

  
  const cardCheck =
    insuranceCard.filter(
      (card: any) => !Boolean(card.frontPhoto) && !Boolean(card.backPhoto),
    ).length !== 0;

  const screen = () => {
    switch (state.screen) {
      case Screens.INIT:
        return (
          <div className="form-container">
            <CustomBtnSelect
              label="Do you have health insurance?"
              persist={true}
              options={['Yes', 'No']}
              onClick={(e) => {
                const bool = e === 'Yes' ? true : false;
                handleUpdateUser({ hasInsurance: bool });
                if (!bool) {
                  setState({ screen: Screens.SELFPAY });
                } else {
                  setState({ screen: Screens.DEFAULT });
                }
              }}
            />
          </div>
        );
      case Screens.DEFAULT: {
        return (
          <>
            <div className="form-container">
              {!state.openSMS && (
                <Typography variant="body1" align="center">
                  {state.insuranceCards.length === 0
                    && 'Seems we have no insurance plans on record for you. Tap "Add Insurance Card" below to add a new plan.'
                  }
                </Typography>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>

                <CardList
                  cardList={state.insuranceCards}
                  onInitiateNewCard={handleInitiateNewCard}
                  onEditCard={handleEditCard}
                  onDeleteCard={(index) => setState({ openDelete: index })}
                  onUpload={handleInitiateNewCard}
                  onSetManual={() => {
                    setFormDetails(defaultDetails);
                    setState({ screen: Screens.MANUALADD, edittedCard: null, openSMS: false });
                  }}                  
                  openSMS={state.openSMS}
                  cardCheck={cardCheck}
                />
              </Box>

              {!state.openSMS || state.insuranceCards.length < 1 &&
                <p style={{ marginTop: '1rem' }}>
                  Don't have insurance?{' '}
                  <Button
                    variant="text"
                    onClick={() =>
                      setState({ screen: Screens.INIT, hasInsurance: null })
                    }>
                    Click here to update.
                  </Button>
                </p>
              }
            </div>
            {['/registration', '/scheduling', '/complete-profile', '/confirm-details'].includes(pathname) && (
              <Button
                variant="contained"
                size="medium"
                type="button"
                onClick={() => onContinue(user)}
              >
                Done
              </Button>
            )}
          </>
        );
      }
      case Screens.SELFPAY: {
        return (
          <>
            <div className="form-container">
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="body1">
                  <b>No problem!</b> We have a terrific self pay option. Here is a
                  document explaining how Ognomy self pay works.
                </Typography>
                <Button
                  variant="text"
                  sx={{ width: 'fit-content' }}
                  onClick={() => {
                    window.open(`
                      ${SERVER_URL}/conditions/${
                        patientInLimitedState ? 'limited-self-pay' : 'self-pay'
                      },
                      '_blank',
                    `);
                  }}>
                  Self Pay Document
                </Button>
                <Typography variant="body1">
                  We will also send a copy of this to your email for future
                  reference.
                </Typography>
              </Box>
              <CustomBtnSelect
                onClick={(e) => {
                  const values = { selfPay: e === 'Elect to self-pay' };
                  setState(values);
                  handleUpdateUser(values);
                }}
                persist={true}
                options={['Elect to self-pay', 'Need more time']}
                selected={user.selfPay ? 'Elect to self-pay' : 'Need more time'}
              />
              <Typography variant="body1">
                Have insurance?{' '}
                <Button
                  variant="text"
                  onClick={() => setState({ screen: Screens.INIT })}>
                  Click here to update.
                </Button>
              </Typography>
            </div>
            {(['/registration', '/scheduling', '/complete-profile', '/confirm-details'].includes(pathname)) && (
              <Button
                variant="contained"
                size="medium"
                type="button"
                sx={{ width: 'fit-content', alignSelf: 'center' }}
                onClick={() => onContinue(user)}
              >
                Done
              </Button>
            )}
          </>
        );
      }
      case Screens.EDIT: {
        return (
          <ConfirmDetails
            insuranceInfo={state.edittedCard || formDetails}
            loadingState={loadingState.submittingCard}
            onConfirm={handleConfirm}
            onOpenSMS={() => setState({ openSMS: true })}
            formDetails={formDetails}
            setFormDetails={setFormDetails}
          />

        );
      }
      case Screens.MANUALADD: {
        return (
          <ConfirmDetails
            insuranceInfo={state.edittedCard || formDetails}
            loadingState={loadingState.submittingCard}
            onConfirm={handleConfirm}
            onOpenSMS={() => setState({ openSMS: true })}
            formDetails={formDetails}
            setFormDetails={setFormDetails}
          />
        );
      }
      default: {
        return <></>;
      }
    }
  };

  return (
    <div className="insurance">
      {screen()}

      <SMSModal
        open={state.openSMS}
        onClose={() => setState({ openSMS: false })}
        onSend={async () => {

          const newCard = await handleConfirm({
            frontFilePath: null,
            backFilePath: null,
            details: formDetails,
          });

          if (newCard?._id) {
            setState((prev: any) => ({
              ...prev,
              edittedCard: newCard,
              openSMS: true,
            }));
            
            setUploadSessionStarted(true);

            setLoadingState((prev: any) => ({
              ...prev,
              observing: true,
            }));
          } else {
            setLoadingState({ observing: false });
          }

          return newCard;
        }}
        onDone={() => setState({
          screen: Screens.DEFAULT,
        })}
        onStartPolling={startPollingForExistingCard}
        selectedInsurance={state.edittedCard}
      />

      <DeleteModal
        open={state.openDelete !== null}
        onDelete={handleDeleteCard}
        onClose={() => setState({ openDelete: null })}
      />
    </div>
  );
};

export default Insurance;